import Lenis from '@studio-freight/lenis'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import lottie from 'lottie-web/build/player/lottie_light'
import ScrollAnimation from '../modules/ScrollAnimation.js'
import EntranceAnimation from '../modules/EntranceAnimation.js'
import SectionDots from '../modules/SectionDots.js'
import Navigation from '../modules/Navigation.js'

gsap.registerPlugin(ScrollTrigger)
ScrollTrigger.config({ ignoreMobileResize: true })

// Prevents address bar show/hide on mobile + fixes iOS scroll bugs.
if ('ontouchstart' in window) ScrollTrigger.normalizeScroll(true)

const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
const lenis = isTouch ? null : new Lenis({ smoothWheel: true })
if (lenis) window.lenis = lenis

// Sync ScrollTrigger with Lenis scroll position
if (lenis) {
  ScrollTrigger.scrollerProxy(document.body, {
    scrollTop(value) {
      if (arguments.length && (window.__entranceComplete === true)) lenis.scrollTo(value)
      return lenis.scroll
    },
    getBoundingClientRect() {
      return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
    }
  })
  lenis.on('scroll', ScrollTrigger.update)
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000)
  })
  gsap.ticker.lagSmoothing(0)
}

// Lock scroll until entrance complete
if (lenis) lenis.stop()

/*
* Init
*/
const init = () => {
new EntranceAnimation(lenis, lottie)
  const dateEl = document.querySelector('.js-date')
  if (dateEl) dateEl.textContent = new Date().getFullYear()
  new ScrollAnimation(lenis)
  new SectionDots(lenis)
  new Navigation(lenis)
  initTooltips()
  initContentEndLottie()
  initContentEndVideo()
}

function initTooltips() {
  const tooltips = document.querySelectorAll('.tooltip')
  if (!tooltips.length) return

  // Only use click on devices where hover is unreliable (touch)
  const hoverNone = window.matchMedia('(hover: none)')
  let isClickMode = hoverNone.matches

  const closeActive = () => {
    tooltips.forEach((el) => el.classList.remove('tooltip--active'))
  }

  document.addEventListener('click', (e) => {
    if (!isClickMode) return
    const tooltip = e.target.closest('.tooltip')
    if (tooltip) {
      e.preventDefault()
      const wasActive = tooltip.classList.contains('tooltip--active')
      closeActive()
      if (!wasActive) tooltip.classList.add('tooltip--active')
    } else {
      closeActive()
    }
  })

  hoverNone.addEventListener('change', (mq) => {
    isClickMode = mq.matches
    if (!mq.matches) closeActive()
  })
}

function initContentEndLottie() {
  const triggerEl = document.getElementById('js-content-end')
  const containerEl = document.getElementById('js-content-end-lottie')
  if (!triggerEl || !containerEl) return

  let animation = null
  let shouldPlay = false
  let hasPlayed = false

  const playWhenReady = () => {
    if (animation && shouldPlay && !hasPlayed) {
      hasPlayed = true
      animation.goToAndPlay(0)
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (!entry.isIntersecting) return
      shouldPlay = true
      if (!animation) {
        const path = containerEl.dataset.lottiePath || '/assets/lottie/intro.json'
        try {
          animation = lottie.loadAnimation({
            container: containerEl,
            renderer: 'svg',
            loop: false,
            autoplay: false,
            path: path.includes('?') ? path : `${path}?v=1`
          })
          animation.addEventListener('DOMLoaded', playWhenReady)
          animation.addEventListener('data_failed', () => {
            console.warn('Content-end Lottie: failed to load animation data')
          })
        } catch (e) {
          console.warn('Content-end Lottie load failed', e)
          return
        }
      } else if (!hasPlayed) {
        playWhenReady()
      }
    },
    { threshold: 0.2, rootMargin: '0px' }
  )

  observer.observe(triggerEl)
}

function initContentEndVideo() {
  const playBtn = document.getElementById('js-content-end-play-btn')
  const videoEl = document.getElementById('js-content-end-video-el')
  const inner = document.getElementById('js-content-end-video-inner')
  if (!playBtn || !videoEl || !inner) return

  const img = inner.querySelector('.js-content-end-video-img')
  const lottieEl = document.getElementById('js-content-end-lottie')
  const toHide = [playBtn, img, lottieEl].filter(Boolean)
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const useNativeControlsOnTouch = isTouch

  if (useNativeControlsOnTouch) {
    videoEl.removeAttribute('playsinline')
    videoEl.removeAttribute('webkit-playsinline')
  } else {
    videoEl.setAttribute('playsinline', '')
  }

  const DURATION = 1
  const ease = 'power2.inOut'
  const desktopPlaybackToggleBtn = document.createElement('button')
  desktopPlaybackToggleBtn.type = 'button'
  desktopPlaybackToggleBtn.className = 'content-end__playback-toggle-btn'
  desktopPlaybackToggleBtn.textContent = 'Pause'
  inner.appendChild(desktopPlaybackToggleBtn)

  const setDesktopPlaybackToggleVisibility = (isVisible) => {
    desktopPlaybackToggleBtn.style.pointerEvents = isVisible ? 'auto' : 'none'
    gsap.to(desktopPlaybackToggleBtn, { opacity: isVisible ? 1 : 0, duration: 0.3, ease })
  }

  const syncDesktopPlaybackToggleLabel = () => {
    desktopPlaybackToggleBtn.textContent = videoEl.paused ? 'Play' : 'Pause'
  }

  const transitionToVideo = () => {
    gsap.to(toHide, { opacity: 0, duration: DURATION, ease })
    gsap.to(videoEl, {
      opacity: 1,
      duration: DURATION,
      ease,
      onStart: () => {
        videoEl.classList.add('is-playing')
        videoEl.play()
        syncDesktopPlaybackToggleLabel()
        setDesktopPlaybackToggleVisibility(true)
      }
    })
  }

  const transitionToPoster = () => {
    gsap.to(videoEl, {
      opacity: 0,
      duration: DURATION,
      ease,
      onComplete: () => {
        videoEl.classList.remove('is-playing')
        videoEl.pause()
        videoEl.currentTime = 0
        syncDesktopPlaybackToggleLabel()
        setDesktopPlaybackToggleVisibility(false)
      }
    })
    gsap.to(toHide, { opacity: 1, duration: DURATION, ease })
  }

  if (useNativeControlsOnTouch) {
    playBtn.addEventListener('click', () => {
      if (videoEl.classList.contains('is-playing')) return

      videoEl.classList.add('is-playing')

      const tryOpenNativeFullscreen = () => {
        if (typeof videoEl.webkitEnterFullscreen === 'function') {
          videoEl.webkitEnterFullscreen()
          return
        }
        if (typeof videoEl.requestFullscreen === 'function') {
          videoEl.requestFullscreen().catch(() => {})
        }
      }

      const playPromise = videoEl.play()
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(tryOpenNativeFullscreen).catch(() => {
          videoEl.classList.remove('is-playing')
        })
        return
      }

      tryOpenNativeFullscreen()
    })

    videoEl.addEventListener('ended', () => {
      videoEl.classList.remove('is-playing')
      videoEl.pause()
      videoEl.currentTime = 0
    })
    return
  }

  playBtn.addEventListener('click', () => {
    if (videoEl.classList.contains('is-playing')) return
    transitionToVideo()
  })

  desktopPlaybackToggleBtn.addEventListener('click', () => {
    if (videoEl.paused) {
      const playPromise = videoEl.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    } else {
      videoEl.pause()
    }
    syncDesktopPlaybackToggleLabel()
  })

  videoEl.addEventListener('play', syncDesktopPlaybackToggleLabel)
  videoEl.addEventListener('pause', syncDesktopPlaybackToggleLabel)
  videoEl.addEventListener('ended', transitionToPoster)
}

// Resize scroll reset handled by ScrollAnimation.setScrollDistance

document.addEventListener('DOMContentLoaded', () => {
  // Scroll is released by the entrance-complete lifecycle.
  window.scrollTo(0, 0)
  if (lenis) lenis.scrollTo(0, { immediate: true })
  init()
})